import Cookies from "js-cookie";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { login, teacherLogin, setAccessToken, forgotPasswordCall } from "../helper/apicalls/auth";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import "../styles/Login.scss";
import edluminaHorizontalLogo from "../assets/png/edluminaHorizontalLogo.png";

const Login = () => {
  const navigate = useNavigate();
  const [loginName, setLoginName] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // 'login', 'forgot-password', 'verification-pending'
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotError, setForgotError] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const checkForSecretToken = async () => {
    const searchParams = new URLSearchParams(window.location.search);
    const secretToken = searchParams.get("secret_token");
    const courseKey = searchParams.get("courseKey");
    const batchId = searchParams.get("batchid");

    if (secretToken) {
      setLoading(true);
      try {
        if (courseKey) Cookies.set("courseKey", courseKey);
        if (batchId) Cookies.set("batchId", batchId);

        const response = await teacherLogin(
          secretToken,
          courseKey,
          batchId,
          setError,
        );

        if (response.token && response.admin) {
          setAccessToken(response.token, rememberMe);
          Cookies.set(
            "user",
            JSON.stringify({ admin: true, name: "Admin_Teacher" }),
          );
          Cookies.remove("hasShownPendingPopup");
          if (courseKey) {
            navigate(`/courses/${courseKey}`);
          } else {
            navigate("/dashboard");
          }
        } else {
          handleLoginSuccess(response);
        }
      } catch (err) {
        console.error("Auto-login failed:", err);
        setLoading(false);
      }
      return;
    }

    const token = Cookies.get("accessToken");
    if (token) {
      const storedUser = Cookies.get("user");
      let preferenceFlag;

      if (storedUser) {
        try {
          const parsed = JSON.parse(storedUser);
          preferenceFlag = parsed?.preference;
        } catch (e) {}
      }

      if (preferenceFlag === true) {
        Cookies.remove("hasShownPendingPopup");
        navigate("/dashboard");
      } else {
        navigate("/survey");
      }
    }
  };

  const handleLoginSuccess = (response) => {
    if (response.auth_token) {
      setAccessToken(response.auth_token, rememberMe);

      if (response.user) {
        const mergedUser = {
          ...response.user,
          premium: response.user.premium ?? response.premium ?? false,
          is_ai_eval_enabled: response.user.is_ai_eval_enabled ?? response.is_ai_eval_enabled ?? false,
        };
        Cookies.set("user", JSON.stringify(mergedUser));
      } else {
        const userObj = {
          preference: response.preference,
          premium: response.premium ?? false,
          iqTest_completed: response.iqTest_completed,
          psychometricTest_completed: response.psychometricTest_completed,
          test_count: response.test_count,
          total_coins: response.total_coins,
          total_xp: response.total_xp,
          streak_count: response.streak_count,
          streak_coins: response.streak_coins,
          login_coins: response.login_coins,
          name: response.name,
          email: response.email,
          is_ai_eval_enabled: response.is_ai_eval_enabled ?? false,
        };
        Cookies.set("user", JSON.stringify(userObj));
      }

      if (response.courseKey || response.course_key) {
        const courseKey = response.courseKey || response.course_key;
        Cookies.set("courseKey", courseKey);
      }

      const preferenceFlag = response.preference ?? response.user?.preference;
      const iqTestCompleted =
        response.iqTest_completed ?? response.user?.iqTest_completed;
      const psychometricTestCompleted =
        response.psychometricTest_completed ??
        response.user?.psychometricTest_completed;
      const testCount = response.test_count ?? response.user?.test_count;

      if (!preferenceFlag) {
        navigate("/survey");
        return;
      }

      if (!testCount || testCount === 0) {
        Cookies.remove("hasShownPendingPopup");
        navigate("/dashboard");
        return;
      }

      if (testCount === 1) {
        if (iqTestCompleted || psychometricTestCompleted) {
          Cookies.remove("hasShownPendingPopup");
          navigate("/dashboard");
        } else {
          navigate("/test");
        }
        return;
      }

      if (testCount === 2) {
        if (iqTestCompleted && psychometricTestCompleted) {
          Cookies.remove("hasShownPendingPopup");
          navigate("/dashboard");
        } else {
          navigate("/test");
        }
      }
    } else {
      setError("Invalid response from server");
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && loginName && password) {
      handleSubmit(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loginName || !password) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const registerNumberRegex = /^[a-zA-Z0-9]{5,}$/;

    if (!emailRegex.test(loginName) && !registerNumberRegex.test(loginName)) {
      setEmailError("Enter a valid mail or register number");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const response = await login(loginName, password, setError);

      if (response.auth_token) {
        handleLoginSuccess(response);
      } else {
        setError("Invalid response from server");
      }
    } catch (err) {
      console.error("Login failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError("Enter a valid email address");
      return;
    }

    setForgotError("");
    setForgotLoading(true);

    try {
      const url = window.location.origin;
      await forgotPasswordCall(forgotEmail, url);
      setView("verification-pending");
    } catch (err) {
      console.error("Forgot password failed:", err);
      setForgotError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to send reset link. Please check the email and try again."
      );
    } finally {
      setForgotLoading(false);
    }
  };

  useEffect(() => {
    checkForSecretToken();
  }, [navigate]);

  return (
    <div className="login-page">
      <img
        src={edluminaHorizontalLogo}
        className="navbar-logo-img"
        alt="Edlumina logo"
      />
      <div className="login-container-wrapper">
        <div className="login-container">
          {view === "login" && (
            <>
              <div className="login-header">
                <h1 className="logo-text" style={{ textAlign: "center" }}>
                  Sign in
                </h1>
              </div>
              <form
                className="login-form"
                onSubmit={handleSubmit}
                onKeyDown={handleKeyDown}
              >
                {error && <div className="error-message">{error}</div>}

                <div className="form-group">
                  <label htmlFor="loginName">Login Name</label>
                  <input
                    type="text"
                    id="loginName"
                    placeholder="Enter your login name"
                    value={loginName}
                    onChange={(e) => {
                      setLoginName(e.target.value);
                      setEmailError("");
                    }}
                    required
                    disabled={loading}
                  />
                  {emailError && (
                    <span
                      style={{
                        color: "red",
                        fontSize: "12px",
                        marginTop: "5px",
                        display: "block",
                      }}
                    >
                      {emailError}
                    </span>
                  )}
                </div>
                <div className="form-group password-group">
                  <label htmlFor="password">Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </button>
                  </div>
                </div>
                <div className="form-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      disabled={loading}
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <span>Remember me</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setView("forgot-password");
                      setError("");
                    }}
                    className="forgot-password"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    Forgot Password?
                  </button>
                </div>
                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? "Logging in..." : "Login"}
                </button>
              </form>
            </>
          )}

          {view === "forgot-password" && (
            <>
              <div className="login-header">
                <h1 className="logo-text" style={{ textAlign: "center" }}>
                  Forgot Password
                </h1>
                <p className="login-subtitle" style={{ fontSize: "14px", color: "var(--text-secondary)", marginTop: "8px" }}>
                  Enter your email address to receive a password reset link.
                </p>
              </div>
              <form className="login-form" onSubmit={handleForgotSubmit}>
                {forgotError && <div className="error-message">{forgotError}</div>}

                <div className="form-group">
                  <label htmlFor="forgotEmail">Email Address</label>
                  <input
                    type="email"
                    id="forgotEmail"
                    placeholder="Enter your email address"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      setForgotError("");
                    }}
                    required
                    disabled={forgotLoading}
                  />
                </div>

                <button type="submit" className="login-btn" disabled={forgotLoading}>
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </button>

                <button
                  type="button"
                  className="login-btn"
                  style={{
                    background: "transparent",
                    color: "var(--accent-2, --blue-fill-color)",
                    border: "1px solid var(--accent-2, --blue-fill-color)",
                    marginTop: "10px"
                  }}
                  onClick={() => {
                    setView("login");
                    setForgotEmail("");
                    setForgotError("");
                  }}
                  disabled={forgotLoading}
                >
                  Back to Sign In
                </button>
              </form>
            </>
          )}

          {view === "verification-pending" && (
            <>
              <div className="login-header">
                <h1 className="logo-text" style={{ textAlign: "center", color: "#28a745" }}>
                  Check your email
                </h1>
                <p className="login-subtitle" style={{ fontSize: "14px", color: "var(--text-secondary)", marginTop: "16px", lineHeight: "1.5" }}>
                  We've sent a password reset link to <strong>{forgotEmail}</strong>. Please check your inbox and click the link to reset your password.
                </p>
              </div>
              <button
                type="button"
                className="login-btn"
                style={{ width: "100%", marginTop: "24px" }}
                onClick={() => {
                  setView("login");
                  setForgotEmail("");
                  setForgotError("");
                }}
              >
                Back to Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
